import { ChakraProvider } from '@chakra-ui/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from 'commons/components/layout/ErrorBoundary'
import { PageLoaderCentered } from 'commons/components/layout/PageLoaderCentered'
import 'commons/config/i18n'
import { Toaster } from 'commons/design-system/components/Toaster'
import customTheme from 'commons/design-system/theme'
import 'commons/design-system/theme.css'
import { StrictMode, Suspense, lazy, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			retry: 2,
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
			keepPreviousData: true,
			staleTime: 1000 * 60 * 10,
			cacheTime: 1000 * 60 * 10
		},
		mutations: {
			retry: 4,
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000)
		}
	}
})

const RootRenderer = ({ children }: { children: ReactNode }) => {
	return (
		<StrictMode>
			<ChakraProvider value={customTheme}>
				<QueryClientProvider client={queryClient}>
					<Suspense fallback={<PageLoaderCentered />}>
						<ErrorBoundary>{children}</ErrorBoundary>
						<Toaster />
					</Suspense>
				</QueryClientProvider>
			</ChakraProvider>
		</StrictMode>
	)
}

const LydiaPageRoot = () => {
	const PhoneForm = lazy(() => import('features/lydia-page/LydiaPage'))
	return (
		<RootRenderer>
			<PhoneForm />
		</RootRenderer>
	)
}

const MoneyPotRoot = () => {
	const MoneyPot = lazy(() => import('features/money-pot/MoneyPotPage'))
	return (
		<RootRenderer>
			<MoneyPot />
		</RootRenderer>
	)
}

const SuccessMoneyPotRoot = () => {
	const MoneyPotSuccessPage = lazy(() => import('features/success-page/MoneyPotSuccessPage'))
	return (
		<RootRenderer>
			<MoneyPotSuccessPage />
		</RootRenderer>
	)
}

const SuccessLydiaPageRoot = () => {
	const SuccessLydiaPage = lazy(() => import('features/success-page/LydiaPageSuccessPage'))
	return (
		<RootRenderer>
			<SuccessLydiaPage />
		</RootRenderer>
	)
}

const SuccessTinkPageRoot = () => {
	const SuccessTinkPage = lazy(() => import('features/success-page/TinkSuccessPage'))
	return (
		<RootRenderer>
			<SuccessTinkPage />
		</RootRenderer>
	)
}

const ErrorTinkPageRoot = () => {
	const ErrorTinkPage = lazy(() => import('features/error-page/TinkErrorPage'))
	return (
		<RootRenderer>
			<ErrorTinkPage />
		</RootRenderer>
	)
}

const ClaimTagRoot = () => {
	const ClaimTagPage = lazy(() => import('features/claim-tag/ClaimTagPage'))
	return (
		<RootRenderer>
			<ClaimTagPage />
		</RootRenderer>
	)
}
const lydiaPageRoot = document.querySelector('.phone-form-page')
const moneyPotRoot = document.querySelector('.money-pot-page')
const successPageRoot = document.querySelector('.success-page')
const successLydiaPageRoot = document.querySelector('.success-lydia-page')
const successTinkPageRoot = document.querySelector('.success-tink-page')
const errorTinkPageRoot = document.querySelector('.error-tink-page')
const claimTagRoot = document.querySelector('.claim-tag-page')
const root = createRoot(document.querySelector('#react-root') as HTMLElement)

if (lydiaPageRoot) {
	root.render(<LydiaPageRoot />)
} else if (moneyPotRoot) {
	root.render(<MoneyPotRoot />)
} else if (successPageRoot) {
	root.render(<SuccessMoneyPotRoot />)
} else if (successLydiaPageRoot) {
	root.render(<SuccessLydiaPageRoot />)
} else if (successTinkPageRoot) {
	root.render(<SuccessTinkPageRoot />)
} else if (errorTinkPageRoot) {
	root.render(<ErrorTinkPageRoot />)
} else if (claimTagRoot) {
	root.render(<ClaimTagRoot />)
}
